import { Toast } from '@deque/cauldron-react';
import React from 'react';
import { useAuthContext } from '../contexts/auth';
import useWebStorage, { WebStorageType } from '../hooks/useWebStorage';

export interface PromotionalBannerProps {
  children: React.ReactNode;
  expiresAt?: Date;
  storageKey?: string;
  className?: string;
  dismissed?: 'permanently' | 'session';
  isShownAllowed?: boolean;
}

const PROMOTIONAL_BANNER_STORAGE_KEY = 'promotional_banner_show';

const isExpired = (expirationDate: Date | undefined) => {
  if (!expirationDate) {
    return false;
  }

  const now = new Date();

  return expirationDate <= now;
};

const PromotionalBanner = ({
  children,
  storageKey = PROMOTIONAL_BANNER_STORAGE_KEY,
  expiresAt,
  dismissed = 'permanently',
  className,
  isShownAllowed = true
}: PromotionalBannerProps) => {
  const { user, loading } = useAuthContext();
  const isPermanentDismissal = dismissed === 'permanently';
  const storageType = isPermanentDismissal
    ? WebStorageType.Local
    : WebStorageType.Session;
  const keyByUser = user?.id ? `${storageKey}_${user?.id}` : storageKey;
  // If user data hasn't been loaded yet, the banner should not be shown by default.
  const initialValue = !loading;

  const [isShown, setIsShown] = useWebStorage<boolean>(
    storageType,
    keyByUser,
    initialValue
  );

  const onDismiss = () => setIsShown(false);

  const isPromotionExpired = isExpired(expiresAt);

  if (isPromotionExpired || !isShownAllowed) {
    return null;
  }

  return (
    <Toast
      type="info"
      show={isShown}
      onDismiss={onDismiss}
      className={className}
    >
      {children}
    </Toast>
  );
};

export default PromotionalBanner;
